/** Stock at a location. Always derived from the ledger, never stored. */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AsOnPicker } from "../../components/AsOnPicker";
import { ReportDownload } from "../../components/ReportDownload";
import { api, ApiError } from "../../lib/api";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import type { Location, StockLine, StockResponse } from "../../lib/types";
import { fmtDate, fmtQty, today, toNumber } from "../../lib/format";

/** What each heading sorts, filters and exports by — never what is drawn.
 *  The quantity column shows a chevron on a negative balance and sorts on
 *  the number underneath it. */
const COLUMNS: SheetColumn<StockLine>[] = [
  { key: "item_name", label: "Item", value: (r) => r.item_name },
  { key: "item_code", label: "Code", value: (r) => r.item_code },
  {
    key: "qty",
    label: "Quantity",
    type: "number",
    numeric: true,
    value: (r) => toNumber(r.qty),
    format: (r) => fmtQty(r.qty, 3),
  },
  { key: "uom", label: "Unit", value: (r) => r.uom },
];

export function StockView() {
  // From masters, not a list baked in here. The hardcoded five meant a
  // location added to the plant was invisible on this screen until somebody
  // remembered to edit it — and a retired one stayed forever.
  const [locations, setLocations] = useState<Location[]>([]);
  const [code, setCode] = useState("");
  const [asOn, setAsOn] = useState(today);
  const [data, setData] = useState<StockResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .locations()
      .then((rows) => {
        setLocations(rows);
        setCode((current) => current || rows[0]?.code || "");
      })
      .catch((e: ApiError) => setError(e.message));
  }, []);

  const load = useCallback(() => {
    if (!code) return;
    setData(null);
    setError(null);
    api
      .stock(code, asOn)
      .then(setData)
      .catch((e: ApiError) => setError(e.message));
  }, [code, asOn]);

  useEffect(load, [load]);

  const lines = useMemo(() => data?.lines ?? [], [data]);
  const sheet = useSheet(lines, COLUMNS);

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Stock</h2>
          <p className="page__sub">
            Derived from the ledger at each request — never a stored balance
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Location</span>
            <select value={code} onChange={(e) => setCode(e.target.value)}>
              {locations.map((l) => (
                <option key={l.code} value={l.code}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <AsOnPicker value={asOn} onChange={setAsOn} />
          <ReportDownload title="Stock" period={{ preset: "custom", from: asOn, to: asOn }} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}

      {!data && !error && (
        <div className="state">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="skeleton state__bar" />
          ))}
        </div>
      )}

      {data && data.lines.length === 0 && (
        <div className="state state--empty">
          <span className="state__glyph" aria-hidden="true">
            ◫
          </span>
          <h3>Nothing in stock here</h3>
          <p>
            No movements have reached {data.location_name} on or before{" "}
            {fmtDate(data.as_on)}.
          </p>
        </div>
      )}

      {data && data.lines.length > 0 && (
        <>
          <SheetToolbar sheet={sheet} exportName={`stock-${data.location_code}`}>
            <span className="section-note">
              {data.location_name} as on {fmtDate(data.as_on)}
            </span>
          </SheetToolbar>
          <div className="tablewrap">
            <table className="table">
              <SheetHead sheet={sheet} />
            <tbody>
              {sheet.rows.map((line) => {
                const negative = Number(line.qty) < 0;
                return (
                  <tr key={line.item_id}>
                    <td>{line.item_name}</td>
                    <td className="mono">{line.item_code}</td>
                    <td
                      className={`num mono${negative ? " cell--negative" : ""}`}
                    >
                      {negative ? "▼ " : ""}
                      {fmtQty(line.qty, 3)}
                    </td>
                    <td>{line.uom}</td>
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          {sheet.rows.length === 0 && (
            <p className="section-note">
              No row matches the filters. Clear them to see all{" "}
              {sheet.total} again.
            </p>
          )}
        </>
      )}
    </div>
  );
}
